import { useState, useEffect } from 'react'
import { MerchantLayout } from '../../components/layouts/Layout'
import { useCredits } from '../../hooks/useCredits'
import { Card, Button } from '../../components/common/UI'
import { Coins, ShoppingCart, CheckCircle } from 'lucide-react'
import { stripeService } from '../../services/stripe'
import { formatters } from '../../utils/formatting'

export default function Credits() {
  const { purchaseCredits, getBalance, getPurchaseHistory, loading } = useCredits()
  const [balance, setBalance] = useState(0)
  const [history, setHistory] = useState<any[]>([])
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null)

  const packages = stripeService.getCreditPackages()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    const bal = await getBalance()
    setBalance(bal)
    const hist = await getPurchaseHistory()
    setHistory(hist)
  }

  const handlePurchase = async (packageId: string) => {
    setSelectedPackage(packageId)
    const result = await purchaseCredits(packageId)
    if (result.success) {
      await loadData()
    }
    setSelectedPackage(null)
  }

  return (
    <MerchantLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Credit Management</h1>
          <p className="text-gray-600">Purchase credits to run your ad campaigns</p>
        </div>

        <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Your Credit Balance</p>
              <p className="text-4xl font-bold">{formatters.number(balance)}</p>
              <p className="text-blue-100 text-sm mt-1">credits available</p>
            </div>
            <Coins className="w-16 h-16 text-blue-200" />
          </div>
        </Card>

        <div>
          <h2 className="text-xl font-semibold mb-4">Purchase Credits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="flex flex-col">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold capitalize">{pkg.id}</h3>
                  <div className="my-4">
                    <p className="text-3xl font-bold">{formatters.number(pkg.credits)}</p>
                    <p className="text-sm text-gray-600">credits</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{formatters.currency(pkg.price)}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {formatters.currency(pkg.pricePerCredit)}/credit
                  </p>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  <li className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    {pkg.credits} credits
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    No expiration
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    Use across all ads
                  </li>
                </ul>

                <Button
                  onClick={() => handlePurchase(pkg.id)}
                  loading={loading && selectedPackage === pkg.id}
                  icon={ShoppingCart}
                  className="w-full"
                >
                  Purchase
                </Button>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4">Purchase History</h2>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Credits</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((purchase) => (
                    <tr key={purchase.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        {formatters.date(purchase.purchaseDate)}
                      </td>
                      <td className="py-3 px-4">
                        {formatters.number(purchase.creditsReceived)}
                      </td>
                      <td className="py-3 px-4">
                        {formatters.currency(purchase.amount)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          purchase.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          purchase.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {purchase.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {history.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No purchase history yet
              </div>
            )}
          </Card>
        </div>
      </div>
    </MerchantLayout>
  )
}